import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import { TopupHistorySkeleton } from "./TopupHistorySkeleton";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMobile } from "@/hooks/use-mobile";
import { TopupHistoryCard } from "./TopupHistoryCard";
import { DesktopTopupHistoryList } from "./DesktopTopupHistoryList";
import { TopupHistory } from "@/services/topup.service";
import type { TopupHistoryData } from "@/types/topup/history";

const PAGE_SIZE = 5;

const PAGER_BTN = "rounded-2xl bg-card size-14 border-border";

export function TopupHistoryList() {
  const [page, setPage] = useState(1);
  const [topups, setTopups] = useState<TopupHistoryData | null>(null);
  const [loading, setLoading] = useState(true);

  const isMobile = useMobile();
  const visibleCount = isMobile ? 2 : 4;
  const history = topups?.topUpHistoryList ?? [];

  useEffect(() => {
    let isMounted = true;

    const fetchTopupHistory = async () => {
      try {
        setLoading(true);

        const response = await TopupHistory({
          agentCompanySellerId:
            useAuthStore.getState().user?.agentCompanySellerId ?? "",
          pageNo: page,
          pageSize: PAGE_SIZE,
        });

        if (!isMounted) {
          return;
        }

        setTopups(response.data);
      } catch (error) {
        if (isMounted) {
          toast.error(
            error instanceof Error
              ? error.message
              : "Failed to load top-up history.",
          );

          setTopups(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTopupHistory();

    return () => {
      isMounted = false;
    };
  }, [page]);

  if (loading) {
    return <TopupHistorySkeleton />;
  }

  function getPageItems(
    current: number,
    total: number,
    visibleCount: number,
  ): (number | "ellipsis")[] {
    const range = (s: number, e: number) =>
      Array.from({ length: e - s + 1 }, (_, i) => s + i);

    if (total <= visibleCount) {
      return range(1, total);
    }

    const windowStart = Math.max(1, current - 1);
    const windowEnd = Math.min(windowStart + visibleCount - 1, total);

    const items: (number | "ellipsis")[] = range(windowStart, windowEnd);

    if (windowEnd < total) {
      items.push("ellipsis");
    }

    return items;
  }

  const totalRows = topups?.totalRows ?? 0;
  const totalPages = Math.ceil(totalRows / PAGE_SIZE);
  const start = (page - 1) * PAGE_SIZE;

  const goTo = (next: number) =>
    setPage(Math.min(Math.max(1, next), totalPages));

  return (
  <>
    {isMobile ? (
      <div className="mt-2 flex flex-col gap-2">
        {history.length > 0 ? (
          history.map((topup, index) => (
            <TopupHistoryCard
              key={`${topup.topUpNo}-${start + index}`}
              topup={topup}
            />
          ))
        ) : (
          <div className="rounded-2xl bg-card p-4 text-center shadow-xl">
            <p className="text-sm text-muted-foreground">
              No top-up history found.
            </p>
          </div>
        )}
      </div>
    ) : (
      <div className="mt-2">
        {/* Table Header */}
        <div className="mt-2 grid grid-cols-12 items-center gap-4 rounded-t-2xl bg-primary px-6 py-3">
          <div className="col-span-2 text-sm font-medium text-card">
            Top-up no.
          </div>

          <div className="col-span-2 text-center text-sm font-medium text-card">
            E-Invoice Status
          </div>

          <div className="text-sm font-medium text-card">
            Price
          </div>

          <div className="col-span-2 text-center text-sm font-medium text-card">
            Pax.
          </div>

          <div className="col-span-3 text-sm font-medium text-card">
            Date. Time
          </div>

          <div className="col-span-1 text-right text-sm font-medium text-card">
            Action
          </div>
        </div>

        {/* Table Body */}
        <div className="flex flex-col">
          <div className="overflow-hidden rounded-b-2xl bg-card shadow-xl md:px-6">
            {history.length > 0 ? (
              history.map((topup, index) => (
                <div
                  key={`${topup.topUpNo}-${start + index}`}
                  className={index > 0 ? "border-t border-border" : ""}
                >
                  <DesktopTopupHistoryList topup={topup} />
                </div>
              ))
            ) : (
              <div className="flex py-2 items-center justify-center">
                <p className="text-sm text-muted-foreground">
                  No top-up history found.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Pagination only when there is data */}
    {history.length > 0 && (
      <>
        <Pagination className="mt-6">
          <PaginationContent className="w-full gap-4">
            <PaginationItem>
              <PaginationPrevious
                text=""
                onClick={() => goTo(page - 1)}
                aria-disabled={page === 1}
                className={cn(
                  PAGER_BTN,
                  page === 1
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-primary-soft",
                )}
              />
            </PaginationItem>

            <li className="flex flex-1 items-center justify-center gap-4">
              {getPageItems(page, totalPages, visibleCount).map(
                (item, index) =>
                  item === "ellipsis" ? (
                    <PaginationEllipsis
                      className={cn("border", PAGER_BTN)}
                      key={`ellipsis-${index}`}
                    />
                  ) : (
                    <PaginationLink
                      key={item}
                      className={cn(
                        `${PAGER_BTN} text-sm font-bold`,
                        item === page
                          ? "bg-primary text-card hover:bg-primary hover:text-card"
                          : "bg-card text-secondary-foreground hover:bg-primary-soft",
                      )}
                      isActive={item === page}
                      onClick={() => goTo(item)}
                    >
                      {item}
                    </PaginationLink>
                  ),
              )}
            </li>

            <PaginationItem>
              <PaginationNext
                text=""
                onClick={() => goTo(page + 1)}
                aria-disabled={page === totalPages}
                className={cn(
                  PAGER_BTN,
                  page === totalPages
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-primary-soft",
                )}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>

        <p className="mt-2.5 text-center text-xs font-medium text-field">
          Showing {start + 1}-
          {start + history.length} of {totalRows}
        </p>
      </>
    )}
  </>
);
}

function PaginationPrevious({
  className,
  text = "Previous",
  ...props
}: React.ComponentProps<typeof PaginationLink> & {
  text?: string;
}) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn(className)}
      {...props}
    >
      <ChevronLeft className="size-5" />
      {text && <span className="hidden sm:block">{text}</span>}
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  text = "Next",
  ...props
}: React.ComponentProps<typeof PaginationLink> & {
  text?: string;
}) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn(className)}
      {...props}
    >
      {text && <span className="hidden sm:block">{text}</span>}
      <ChevronRight className="size-5" />
    </PaginationLink>
  );
}
